import { useState } from 'react'
import { Link } from 'react-router-dom'
import styles from './LandingPage.module.css'
import logo from '@/assets/logo.jpg'

interface FaqItem {
    question: string
    answer: string
}

export default function LandingPage() {
    const [openFaq, setOpenFaq] = useState<number | null>(null)

    const toggleFaq = (index: number) => {
        setOpenFaq(openFaq === index ? null : index)
    }

    const services = [
        {
            icon: '🤖',
            title: 'AI-Powered Chat Therapy',
            description: 'Have meaningful, therapeutic conversations with our empathetic AI trained by mental health professionals. Available 24/7, no appointments needed.'
        },
        {
            icon: '📋',
            title: 'Clinical Assessments',
            description: 'Take validated psychological assessments including PHQ-9, GAD-7, and more. Track your mental health progress with professional insights.'
        },
        {
            icon: '👨‍⚕️',
            title: 'Specialist Matching',
            description: 'Get matched with licensed therapists and psychiatrists based on your specific needs, preferences, and availability.'
        },
        {
            icon: '🚨',
            title: 'Crisis Intervention',
            description: 'Our intelligent system detects crisis situations and provides immediate access to emergency resources and helplines.'
        },
        {
            icon: '📊',
            title: 'Progress Analytics',
            description: 'Visual dashboards tracking your emotional patterns, therapy session insights, and personalized wellness recommendations.'
        },
        {
            icon: '📚',
            title: 'Wellness Library',
            description: 'Access hundreds of evidence-based articles, guided meditations, breathing exercises, and self-help tools curated by experts.'
        }
    ]

    const testimonials = [
        {
            stars: '⭐⭐⭐⭐⭐',
            text: '"I was skeptical about online therapy, but Shweta Heals changed my perspective. The AI understood my anxiety in ways I couldn\'t explain to friends. It gave me the courage to finally see a therapist."',
            name: 'Priya S.',
            role: 'Mumbai, User since 2024',
            avatar: '👩'
        },
        {
            stars: '⭐⭐⭐⭐⭐',
            text: '"As a working professional, finding time for therapy was impossible. With Shweta Heals, I can process my thoughts at midnight if needed. The specialist matching connected me with an amazing counselor."',
            name: 'Rahul M.',
            role: 'Bangalore, 6 months user',
            avatar: '👨'
        },
        {
            stars: '⭐⭐⭐⭐⭐',
            text: '"The breathing exercises and mood tracking have become part of my daily routine. My social anxiety has reduced significantly, and I finally feel in control of my mental health."',
            name: 'Ananya K.',
            role: 'Delhi, User since 2023',
            avatar: '👩'
        }
    ]

    const trustIndicators = [
        {
            icon: '🔒',
            title: '100% Confidential',
            description: 'End-to-end encryption'
        },
        {
            icon: '👨‍⚕️',
            title: 'Licensed Experts',
            description: 'Verified professionals'
        },
        {
            icon: '⏰',
            title: '24/7 Available',
            description: 'Always here for you'
        },
        {
            icon: '📖',
            title: 'Evidence-Based',
            description: 'Clinically validated'
        },
        {
            icon: '🛡️',
            title: 'HIPAA Compliant',
            description: 'Healthcare-grade security'
        }
    ]

    const faqs: FaqItem[] = [
        {
            question: 'What makes Shweta Heals different from other mental health apps?',
            answer: 'Shweta Heals combines AI-powered therapeutic conversations with seamless access to licensed professionals. Our AI is trained by mental health experts to provide evidence-based support, while our specialist matching ensures you find the right therapist when needed. Unlike generic chatbots, we offer a complete mental health ecosystem.'
        },
        {
            question: 'Is my information kept completely private?',
            answer: 'Absolutely. Privacy is our foundation. All conversations are encrypted end-to-end, we never share data with third parties, and our platform is HIPAA-compliant. Your mental health journey is entirely confidential—only you decide if and when to involve a human therapist.'
        },
        {
            question: 'Can the AI actually help with serious mental health issues?',
            answer: 'Our AI provides valuable support for stress, anxiety, mild to moderate depression, and daily emotional challenges. For serious mental health conditions, the AI serves as a first point of contact and actively guides you toward professional help. Our crisis detection ensures immediate intervention when needed.'
        },
        {
            question: 'How do I get matched with a specialist?',
            answer: 'After interacting with our AI, you can request specialist matching. We collect information about your preferences, specific concerns, preferred therapy style, and schedule. Our algorithm then matches you with verified, licensed mental health professionals who best fit your needs.'
        },
        {
            question: 'What qualifications do your mental health specialists have?',
            answer: 'All specialists on our platform are licensed clinical psychologists, psychiatrists, or certified counselors. Each goes through a rigorous verification process including credential checks, background verification, and interview evaluation before joining our network.'
        },
        {
            question: 'Is there a free trial available?',
            answer: 'Yes! We offer free AI chat sessions so you can experience the platform before committing. Basic features including the resource library and breathing exercises are always free. Premium features like unlimited sessions and specialist consultations are available through affordable subscription plans.'
        }
    ]

    return (
        <div className={styles.landingContainer}>
            {/* Navigation */}
            <nav className={styles.nav}>
                <Link to="/" className={styles.logo}>
                    <img src={logo} alt="ShwetaHeals" className={styles.logoImage} />
                    <span className={styles.logoText}>ShwetaHeals</span>
                </Link>
                <div className={styles.navLinks}>
                    <a href="#about" className={styles.navLink}>About</a>
                    <a href="#services" className={styles.navLink}>Services</a>
                    <a href="#testimonials" className={styles.navLink}>Testimonials</a>
                    <a href="#faq" className={styles.navLink}>FAQ</a>
                    <Link to="/login" className={styles.navCta}>Get Started</Link>
                </div>
            </nav>

            {/* Hero Section */}
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <div className={styles.heroText}>
                        <div className={styles.heroBadge}>
                            ✨ Early Access — Join our growing community
                        </div>
                        <h1 className={styles.heroTitle}>
                            Begin Your <span className={styles.heroHighlight}>Mental Wellness</span> Journey Today
                        </h1>
                        <p className={styles.heroSubtitle}>
                            Get compassionate, AI-powered mental health support available 24/7.
                            Talk to our empathetic assistant, access professional resources,
                            and connect with licensed therapists when you're ready.
                        </p>
                        <div className={styles.heroCtas}>
                            <Link to="/register" className={styles.ctaPrimary}>
                                Start Free Consultation →
                            </Link>
                            <a href="#about" className={styles.ctaSecondary}>
                                Learn How It Works
                            </a>
                        </div>
                        <div className={styles.heroStats}>
                            <div className={styles.heroStat}>
                                <div className={styles.heroStatNumber}>10+</div>
                                <div className={styles.heroStatLabel}>Active Users</div>
                            </div>
                            <div className={styles.heroStat}>
                                <div className={styles.heroStatNumber}>5</div>
                                <div className={styles.heroStatLabel}>Specialists</div>
                            </div>
                            <div className={styles.heroStat}>
                                <div className={styles.heroStatNumber}>4.9★</div>
                                <div className={styles.heroStatLabel}>User Rating</div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.heroVisual}>
                        <div className={styles.heroImageContainer}>
                            <div className={styles.heroImage}>🧘</div>
                            <div className={`${styles.floatingCard} ${styles.floatingCard1}`}>
                                <span className={styles.floatingCardIcon}>✅</span>
                                <span className={styles.floatingCardText}>Session Complete</span>
                            </div>
                            <div className={`${styles.floatingCard} ${styles.floatingCard2}`}>
                                <span className={styles.floatingCardIcon}>💬</span>
                                <span className={styles.floatingCardText}>24/7 Support</span>
                            </div>
                            <div className={`${styles.floatingCard} ${styles.floatingCard3}`}>
                                <span className={styles.floatingCardIcon}>🔒</span>
                                <span className={styles.floatingCardText}>100% Private</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Bar */}
            <section className={styles.statsBar}>
                <div className={styles.statsBarContent}>
                    <div className={styles.statBarItem}>
                        <div className={styles.statBarNumber}>5K+</div>
                        <div className={styles.statBarLabel}>Messages Exchanged</div>
                    </div>
                    <div className={styles.statBarItem}>
                        <div className={styles.statBarNumber}>89%</div>
                        <div className={styles.statBarLabel}>Report Improvement</div>
                    </div>
                    <div className={styles.statBarItem}>
                        <div className={styles.statBarNumber}>25 min</div>
                        <div className={styles.statBarLabel}>Avg. Session Time</div>
                    </div>
                    <div className={styles.statBarItem}>
                        <div className={styles.statBarNumber}>24/7</div>
                        <div className={styles.statBarLabel}>Always Available</div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className={styles.about}>
                <div className={styles.sectionContainer}>
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.sectionLabel}>About Us</span>
                    </div>
                    <h2 className={styles.sectionTitle}>A Safe Space for Healing</h2>
                    <p className={styles.sectionSubtitle}>
                        Founded by mental health professionals, Shweta Heals makes quality therapy accessible to everyone.
                    </p>

                    <div className={styles.aboutContent}>
                        <div className={styles.aboutImage}>
                            <div className={styles.aboutImageBg}></div>
                            👩‍⚕️
                        </div>
                        <div className={styles.aboutText}>
                            <h3>Bridging Technology and Mental Health Care</h3>
                            <p>
                                Shweta Heals was born from a simple observation: millions of people need mental
                                health support but face barriers like stigma, cost, and accessibility. We built
                                a platform that breaks down these barriers.
                            </p>
                            <p>
                                Our AI-powered assistant is developed in collaboration with licensed psychologists
                                and psychiatrists, ensuring every interaction is grounded in clinical best practices.
                                When you need human support, our network of verified specialists is ready to help.
                            </p>
                            <p>
                                Whether it's 3 AM anxiety, work stress, relationship challenges, or deeper
                                emotional struggles—we're here to support your journey to mental wellness.
                            </p>
                            <div className={styles.aboutHighlights}>
                                <span className={styles.highlight}>✨ Empathetic AI</span>
                                <span className={styles.highlight}>🧠 Evidence-Based</span>
                                <span className={styles.highlight}>💝 Judgment-Free</span>
                                <span className={styles.highlight}>🌏 India-Focused</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className={styles.howItWorks}>
                <div className={styles.sectionContainer}>
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.sectionLabel}>How It Works</span>
                    </div>
                    <h2 className={styles.sectionTitle}>Three Simple Steps to Wellness</h2>
                    <p className={styles.sectionSubtitle}>
                        Getting support has never been easier. Start your healing journey today.
                    </p>

                    <div className={styles.stepsGrid}>
                        <div className={styles.stepCard}>
                            <div className={styles.stepNumber}>1</div>
                            <div className={styles.stepIcon}>💬</div>
                            <h3 className={styles.stepTitle}>Share Your Story</h3>
                            <p className={styles.stepDescription}>
                                Open up to our AI assistant in a completely safe and confidential space.
                                There's no judgment—just understanding, validation, and support whenever you need it.
                            </p>
                        </div>

                        <div className={styles.stepCard}>
                            <div className={styles.stepNumber}>2</div>
                            <div className={styles.stepIcon}>🎯</div>
                            <h3 className={styles.stepTitle}>Get Personalized Insights</h3>
                            <p className={styles.stepDescription}>
                                Receive guidance tailored to your unique situation. Our AI provides
                                coping strategies, mindfulness exercises, and actionable insights based on your needs.
                            </p>
                        </div>

                        <div className={styles.stepCard}>
                            <div className={styles.stepNumber}>3</div>
                            <div className={styles.stepIcon}>🤝</div>
                            <h3 className={styles.stepTitle}>Connect When Ready</h3>
                            <p className={styles.stepDescription}>
                                When you're ready for human support, we seamlessly match you with licensed
                                therapists who specialize in your areas of concern. No awkward transitions.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section id="services" className={styles.services}>
                <div className={styles.sectionContainer}>
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.sectionLabel}>Our Services</span>
                    </div>
                    <h2 className={styles.sectionTitle}>Comprehensive Mental Health Support</h2>
                    <p className={styles.sectionSubtitle}>
                        Everything you need for your mental wellness journey in one platform
                    </p>

                    <div className={styles.servicesGrid}>
                        {services.map((service, index) => (
                            <div key={index} className={styles.serviceCard}>
                                <div className={styles.serviceIcon}>{service.icon}</div>
                                <h3 className={styles.serviceTitle}>{service.title}</h3>
                                <p className={styles.serviceDescription}>{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section id="testimonials" className={styles.testimonials}>
                <div className={styles.sectionContainer}>
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.sectionLabel} style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }}>Testimonials</span>
                    </div>
                    <h2 className={styles.sectionTitle} style={{ color: 'white' }}>
                        Stories of Healing
                    </h2>
                    <p className={styles.sectionSubtitle} style={{ color: 'rgba(255,255,255,0.9)' }}>
                        Hear from real people whose lives have been transformed
                    </p>

                    <div className={styles.testimonialsGrid}>
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className={styles.testimonialCard}>
                                <div className={styles.testimonialStars}>{testimonial.stars}</div>
                                <p className={styles.testimonialText}>{testimonial.text}</p>
                                <div className={styles.testimonialAuthor}>
                                    <div className={styles.testimonialAvatar}>{testimonial.avatar}</div>
                                    <div className={styles.testimonialInfo}>
                                        <h4>{testimonial.name}</h4>
                                        <p>{testimonial.role}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trust Section */}
            <section className={styles.trust}>
                <div className={styles.sectionContainer}>
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.sectionLabel}>Why Trust Us</span>
                    </div>
                    <h2 className={styles.sectionTitle}>Built on Trust and Expertise</h2>
                    <p className={styles.sectionSubtitle}>
                        Your safety and privacy are our highest priorities
                    </p>

                    <div className={styles.trustGrid}>
                        {trustIndicators.map((item, index) => (
                            <div key={index} className={styles.trustItem}>
                                <div className={styles.trustIcon}>{item.icon}</div>
                                <h3 className={styles.trustTitle}>{item.title}</h3>
                                <p className={styles.trustDescription}>{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className={styles.faq}>
                <div className={styles.sectionContainer}>
                    <div style={{ textAlign: 'center' }}>
                        <span className={styles.sectionLabel}>FAQ</span>
                    </div>
                    <h2 className={styles.sectionTitle}>Common Questions</h2>
                    <p className={styles.sectionSubtitle}>
                        Everything you need to know about Shweta Heals
                    </p>

                    <div className={styles.faqGrid}>
                        {faqs.map((faq, index) => (
                            <div key={index} className={styles.faqItem}>
                                <button
                                    className={styles.faqQuestion}
                                    onClick={() => toggleFaq(index)}
                                >
                                    {faq.question}
                                    <span className={`${styles.faqArrow} ${openFaq === index ? styles.open : ''}`}>
                                        ▼
                                    </span>
                                </button>
                                <div className={`${styles.faqAnswer} ${openFaq === index ? styles.open : ''}`}>
                                    <p className={styles.faqAnswerText}>{faq.answer}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className={styles.ctaSection}>
                <div className={styles.ctaContent}>
                    <h2 className={styles.ctaTitle}>Your Healing Starts Now</h2>
                    <p className={styles.ctaText}>
                        Join our early users who are finding support, healing, and hope through our platform. Take the first step towards a healthier mind.
                    </p>
                    <Link to="/register" className={styles.ctaButton}>
                        Begin Your Free Journey →
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className={styles.footer}>
                <div className={styles.footerContent}>
                    <div className={styles.footerBrand}>
                        <div className={styles.footerLogo}>
                            <img src={logo} alt="ShwetaHeals" className={styles.footerLogoImage} />
                            <span className={styles.footerLogoText}>ShwetaHeals</span>
                        </div>
                        <p className={styles.footerTagline}>
                            Compassionate mental health support for everyone in India.
                            Your journey to wellness starts with a single conversation.
                        </p>
                    </div>

                    <div className={styles.footerSection}>
                        <h4>Quick Links</h4>
                        <ul>
                            <li><a href="#about">About Us</a></li>
                            <li><a href="#services">Services</a></li>
                            <li><a href="#testimonials">Testimonials</a></li>
                            <li><a href="#faq">FAQ</a></li>
                            <li><Link to="/login">Login</Link></li>
                        </ul>
                    </div>

                    <div className={styles.footerSection}>
                        <h4>Resources</h4>
                        <ul>
                            <li><a href="#services">Mental Health Articles</a></li>
                            <li><a href="#services">Self-Help Tools</a></li>
                            <li><a href="#services">Breathing Exercises</a></li>
                            <li><a href="#services">Mindfulness Guides</a></li>
                        </ul>
                    </div>

                    <div className={styles.footerSection}>
                        <h4>Crisis Helplines</h4>
                        <ul>
                            <li><a href="tel:112">Emergency: 112</a></li>
                            <li><a href="tel:1800599099">iCALL: 1800-599-099</a></li>
                            <li><a href="tel:18602662345">Vandrevala: 1860-2662-345</a></li>
                            <li><a href="tel:08046110007">NIMHANS: 080-46110007</a></li>
                        </ul>
                    </div>
                </div>

                <div className={styles.footerBottom}>
                    <p>© 2025 Shweta Heals. Made with 💜 in India for mental wellness.</p>
                    <div className={styles.footerSocials}>
                        <a href="#" className={styles.socialLink}>📘</a>
                        <a href="#" className={styles.socialLink}>🐦</a>
                        <a href="#" className={styles.socialLink}>📸</a>
                        <a href="#" className={styles.socialLink}>💼</a>
                    </div>
                </div>
            </footer>
        </div>
    )
}
